import { createTheme, type Theme } from '@mui/material/styles';

/**
 * Theme.
 *
 * Kept deliberately small — palette, font, radius, a couple of component
 * defaults. The visual identity lives in the shared primitives in
 * src/components/primitives.tsx, not in a sprawling theme object.
 *
 * The one hard rule: components must read colours from the theme
 * (`background.paper`, `text.secondary`, `divider`) or from ACCENTS below.
 * Never ternary on the theme id the way click2gro does — it repeats
 * `currentTheme === 'white' ? ... : ...` about twenty times across its layout,
 * which is why adding a theme there means touching every component.
 */

/**
 * The themes click2gro's web app offers, minus White. Every Silverline page was
 * built for a dark surface, so a light theme needs a page-by-page pass first.
 * `midnight` is click2gro's "Default".
 *
 * Every coloured theme is deeper than click2gro's. Tiles and stat cards are
 * translucent accent tints over background.default, and click2gro's mid-tone
 * backgrounds (plum #4a3d5c, slate #263238, orange #cc5500...) muted every one
 * of them. Each theme keeps its hue in a near-black background and puts the
 * strong colour in the top bar gradient and primary instead.
 */
export const THEME_IDS = [
    'midnight',
    'charcoal',
    'blue',
    'dark',
    'slate',
    'plum',
    'violet',
    'red',
    'green',
    'orange',
    'yellow',
] as const;
export type ThemeId = (typeof THEME_IDS)[number];

interface ThemeDefinition {
    readonly label: string;
    readonly primary: string;
    readonly secondary: string;
    /** Top bar gradient endpoints. */
    readonly navBar: string;
    readonly navBarEnd: string;
    readonly background: { readonly default: string; readonly paper: string };
    /** Picker swatch. */
    readonly swatch: string;
}

const THEMES: Readonly<Record<ThemeId, ThemeDefinition>> = {
    midnight: {
        label: 'Default',
        primary: '#1565c0',
        secondary: '#2e7d32',
        navBar: '#0a1929',
        navBarEnd: '#1a3a6e',
        background: { default: '#0a1929', paper: '#132f4c' },
        swatch: '#0a1929',
    },
    charcoal: {
        label: 'Charcoal',
        primary: '#2e7d32',
        secondary: '#ff8f00',
        navBar: '#000000',
        navBarEnd: '#1a2a1a',
        background: { default: '#121212', paper: '#1e1e1e' },
        swatch: '#121212',
    },
    blue: {
        label: 'Blue',
        primary: '#90caf9',
        secondary: '#2e7d32',
        navBar: '#0a1929',
        navBarEnd: '#1a3a6e',
        background: { default: '#0a1929', paper: '#132f4c' },
        swatch: '#132f4c',
    },
    dark: {
        label: 'Dark',
        primary: '#2e7d32',
        secondary: '#ff8f00',
        navBar: '#000000',
        navBarEnd: '#0d1a0d',
        background: { default: '#000000', paper: '#121212' },
        swatch: '#000000',
    },
    slate: {
        label: 'Slate',
        primary: '#78909c',
        secondary: '#80cbc4',
        navBar: '#11191d',
        navBarEnd: '#3e525c',
        background: { default: '#0f171b', paper: '#1c272d' },
        swatch: '#37474f',
    },
    plum: {
        label: 'Plum',
        primary: '#ab47bc',
        secondary: '#2ecc71',
        navBar: '#1f1230',
        navBarEnd: '#5a2d82',
        background: { default: '#1a1025', paper: '#2a1b3d' },
        swatch: '#3b2257',
    },
    violet: {
        label: 'Violet',
        primary: '#7e57c2',
        secondary: '#2ecc71',
        navBar: '#170f2e',
        navBarEnd: '#5c35a0',
        background: { default: '#130d24', paper: '#221a3d' },
        swatch: '#4a2d80',
    },
    red: {
        label: 'Red',
        primary: '#e53935',
        secondary: '#2ecc71',
        navBar: '#240c0c',
        navBarEnd: '#8b1f1f',
        background: { default: '#1c0b0b', paper: '#311515' },
        swatch: '#7b1a1a',
    },
    green: {
        label: 'Green',
        primary: '#43a047',
        secondary: '#ffcc02',
        navBar: '#0d2214',
        navBarEnd: '#2e7d32',
        background: { default: '#0b1a10', paper: '#152c1d' },
        swatch: '#2e7d32',
    },
    orange: {
        label: 'Orange',
        primary: '#ef6c00',
        secondary: '#2ecc71',
        navBar: '#271306',
        navBarEnd: '#b84d00',
        background: { default: '#1c1007', paper: '#301d0d' },
        swatch: '#cc5500',
    },
    yellow: {
        label: 'Yellow',
        primary: '#f9a825',
        secondary: '#2ecc71',
        navBar: '#211e08',
        navBarEnd: '#9a8500',
        background: { default: '#181606', paper: '#2a270c' },
        swatch: '#a89500',
    },
};

export const DEFAULT_THEME_ID: ThemeId = 'midnight';

export function isThemeId(value: unknown): value is ThemeId {
    return typeof value === 'string' && (THEME_IDS as readonly string[]).includes(value);
}

export const themeOptions = THEME_IDS.map((id) => ({
    id,
    label: THEMES[id].label,
    swatch: THEMES[id].swatch,
}));

export function themeBackground(id: ThemeId): string {
    return THEMES[id].background.default;
}

/**
 * Accent colours for stat cards and module tiles.
 *
 * Named by role rather than by module, so a module can be recoloured without
 * inventing a new hex. Bright enough to read as a 2px border on a dark card.
 */
export const ACCENTS = {
    blue: '#00d1ff',
    green: '#21ff9c',
    amber: '#ffb21c',
    pink: '#ff5fa2',
    purple: '#a05bff',
    teal: '#00bfa5',
    red: '#ff5a5f',
    indigo: '#5c7cfa',
} as const;

export type AccentName = keyof typeof ACCENTS;

/** Connection / state colours, shared by the status pill and alerts. */
export const STATUS_COLOURS = {
    ok: '#66bb6a',
    error: '#ef5350',
    warn: '#ffb21c',
} as const;

export function navBarGradient(id: ThemeId): string {
    const def = THEMES[id];
    return `linear-gradient(135deg, ${def.navBar} 0%, ${def.navBarEnd} 100%)`;
}

export function buildTheme(id: ThemeId): Theme {
    const def = THEMES[id];
    return createTheme({
        palette: {
            mode: 'dark',
            primary: { main: def.primary },
            secondary: { main: def.secondary },
            background: def.background,
        },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        },
        shape: { borderRadius: 8 },
        components: {
            MuiButton: {
                styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
            },
            // Depth in this design comes from borders and coloured glows, never
            // from grey drop shadows.
            MuiPaper: {
                defaultProps: { elevation: 0 },
            },
        },
    });
}

// ─── Skillset chip legibility ───────────────────────────────────────────────

/** WCAG relative luminance of a #rrggbb colour. */
function luminance(hex: string): number {
    const c = hex.replace('#', '');
    const channel = (i: number): number => {
        const v = parseInt(c.slice(i, i + 2), 16) / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
    const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return ((light ?? 0) + 0.05) / ((dark ?? 0) + 0.05);
}

/** Below this, body text is not reliably readable. WCAG AA for normal text. */
const MIN_CONTRAST = 4.5;

/**
 * The text colour for a staff chip filled with their skillset colour.
 *
 * Prefers `staff_title.colour_text`, because that is the office's own choice
 * and the two screens must agree. Falls back to an automatic contrast colour
 * only where the stored pair is genuinely unreadable.
 *
 * It is needed because every one of the twelve skillsets stores BLACK text, and
 * two of them pair it with a dark fill:
 *
 *     2nd Year Apprentice   #3C0AFF on black   ratio 2.68
 *     4th Year Apprentice   #860FFF on black   ratio 3.53
 *
 * Both fail AA (4.5). This is a data problem, fixable on the Skillsets screen
 * by setting those two to white text — at which point this function simply
 * stops firing. It is NOT a licence to recolour chips generally: the other ten
 * pairs are fine and are used exactly as stored.
 *
 * The same two rows are drawn unreadably on the office board today
 * (components/calendar/JobGrid.tsx:228), which does not have this guard.
 */
export function readableInk(fill: string, stored: string | null, fallback: string): string {
    if (stored === null || !/^#[0-9a-fA-F]{6}$/.test(stored) || !/^#[0-9a-fA-F]{6}$/.test(fill)) {
        return fallback;
    }
    return contrastRatio(fill, stored) >= MIN_CONTRAST ? stored : fallback;
}
